import React, { useEffect, useState, useRef } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "./App.css";
import backgroundVideo from "./assets/1.mp4";
import backgroundAudio from "./assets/1.mp3";
import userProfile from "./assets/user-profile.jpg";
import astronautSpinner from "./assets/astronaut.svg";
import cursorImage from "./assets/cursor.png";

// Import all SVG icons
import discordIcon from "./assets/discord.svg";
import steamIcon from "./assets/steam.svg";
import gamepadIcon from "./assets/gamepad.svg";
import globeIcon from "./assets/globe.svg";
import blogIcon from "./assets/blog.svg";
import twitterIcon from "./assets/twitter.svg";
import riotIcon from "./assets/riot.svg";
import blizzardIcon from "./assets/blizzard.svg";
import xboxIcon from "./assets/xbox.svg";
import playstationIcon from "./assets/playstation.svg";

// Define the icon mapping with imported SVGs
const iconMapping = {
  discord: discordIcon,
  steam: steamIcon,
  gamepad: gamepadIcon,
  globe: globeIcon,
  blog: blogIcon,
  twitter: twitterIcon,
  riot: riotIcon,
  blizzard: blizzardIcon,
  xbox: xboxIcon,
  playstation: playstationIcon,
};

// Define the username mapping for accounts
const usernameMapping = {
  discord: "iburtum",
  steam: "iburtum",
  riot: "iburtum#0000",
  blizzard: "Iburtum#2138",
  xbox: "iburtum",
  playstation: "iburtumKSA",
};

// Define the badges with the provided URLs
const badges = [
  { name: "Badge 1", url: "https://cdn.discordapp.com/badge-icons/011940fd013da3f7fb926e4a1cd2e618.png" },
  { name: "Badge 2", url: "https://cdn.discordapp.com/badge-icons/6de6d34650760ba5551a79732e98ed60.png" },
  { name: "Badge 3", url: "https://cdn.discordapp.com/badge-icons/cd5e2cfd9d7f27a8cdcd3e8a8d5dc9f4.png" },
];

// Language content mapping
const content = {
  ar: {
    tabTitle: "حياك",
    clickToEnter: "اضغط للدخول",
    welcomeMessage: "حياك في ملفي الشخصي",
    usernameLabel: "اسم الحساب",
    copyButton: "انسخ",
    copiedMessage: "تم النسخ!",
    copyFailedMessage: "فشل النسخ!",
    discord: "ديسكورد",
    steam: "ستيم",
    gameList: "قائمة الألعاب",
    website: "الموقع",
    blog: "المدونة",
    xAccount: "حساب إكس",
    riotAccount: "حساب رايوت",
    blizzardAccount: "حساب بليزارد",
    xboxAccount: "حساب إكس بوكس",
    playstationAccount: "حساب بلايستيشن",
    lastSeen: "آخر ظهور في",
    languageLabel: "اللغة",
    languageArabic: "العربية",
    languageEnglish: "الإنجليزية",
  },
  en: {
    tabTitle: "Welcome",
    clickToEnter: "Click to Enter",
    welcomeMessage: "Welcome to my profile",
    usernameLabel: "Username",
    copyButton: "Copy",
    copiedMessage: "Copied!",
    copyFailedMessage: "Failed to copy!",
    discord: "Discord",
    steam: "Steam",
    gameList: "Game List",
    website: "Website",
    blog: "Blog",
    xAccount: "X Account",
    riotAccount: "Riot Account",
    blizzardAccount: "Blizzard Account",
    xboxAccount: "Xbox Account",
    playstationAccount: "PlayStation Account",
    lastSeen: "Last seen at",
    languageLabel: "Lan",
    languageArabic: "Ar",
    languageEnglish: "En",
  },
};

const App = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const [discordData, setDiscordData] = useState({
    status: "Loading...",
    rawStatus: "loading",
    lastSeen: "Loading...",
    activity: null,
  });
  const [videoKey, setVideoKey] = useState(Date.now());
  const [volume, setVolume] = useState(0.5);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [popupData, setPopupData] = useState({ username: "", url: "" });
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [copyMessage, setCopyMessage] = useState("");
  const [showClickToEnter, setShowClickToEnter] = useState(false);
  const [language, setLanguage] = useState("ar"); // Default language is Arabic
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);

  const videoRef = useRef(null);
  const audioRef = useRef(null);

  useEffect(() => {
    const handleContextMenu = (e) => e.preventDefault();
    const handleKeyDown = (e) => {
      if (e.key === "F12") e.preventDefault();
      if (e.ctrlKey && e.shiftKey && (e.key === "I" || e.key === "i" || e.key === "J" || e.key === "j" || e.key === "C" || e.key === "c")) {
        e.preventDefault();
      }
      if (e.ctrlKey && (e.key === "U" || e.key === "u")) e.preventDefault();
    };

    document.addEventListener("contextmenu", handleContextMenu);
    document.addEventListener("keydown", handleKeyDown);

    const tabTitle = content[language].tabTitle;
    let currentIndex = 0;
    let typewriterInterval;

    const startTypewriter = () => {
      typewriterInterval = setInterval(() => {
        if (currentIndex < tabTitle.length) {
          document.title = tabTitle.substring(0, currentIndex + 1);
          currentIndex++;
        } else {
          clearInterval(typewriterInterval);
          setTimeout(() => {
            currentIndex = 0;
            document.title = "";
            startTypewriter();
          }, 10000);
        }
      }, 100);
    };

    startTypewriter();

    const images = [userProfile, astronautSpinner];

    const promises = images.map(
      (src) =>
        new Promise((resolve) => {
          const img = new Image();
          img.src = src;
          img.onload = () => {
            console.log(`Successfully loaded image: ${src}`);
            resolve();
          };
          img.onerror = () => {
            console.error(`Failed to load image: ${src}`);
            resolve();
          };
        })
    );

    const resetVideo = () => {
      if (videoRef.current && !videoFailed) {
        videoRef.current.load();
        videoRef.current.play().catch((error) => {
          console.log("Video replay failed:", error);
          setVideoFailed(true);
          setVideoKey(Date.now());
        });
      }
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        resetVideo();
      }
    };

    Promise.all(promises).then(() => {
      setTimeout(() => {
        setIsLoaded(true);
        setShowClickToEnter(true);
        if (videoRef.current) {
          videoRef.current.play().catch((error) => {
            console.log("Initial video autoplay failed:", error);
            setVideoFailed(true);
          });
        }
      }, 3000);
    }).catch(() => {
      setIsLoaded(true);
      setShowClickToEnter(true);
    });

    const fetchDiscordData = async (retries = 3, delay = 2000) => {
      for (let attempt = 1; attempt <= retries; attempt++) {
        try {
          const response = await fetch("https://api.lanyard.rest/v1/users/770727112633876530");
          const data = await response.json();
          console.log("Lanyard API Response:", data);
          if (data.success) {
            const activities = data.data.activities;
            const discordStatus = data.data.discord_status;
            const lastSeen = new Date(data.data.last_seen || Date.now()).toLocaleString(language === "ar" ? "ar-SA" : "en-US", {
              timeZone: "Asia/Riyadh",
              hour: "2-digit",
              minute: "2-digit",
            });

            let statusText = discordStatus === "online" ? "Online" : discordStatus === "dnd" ? "Do Not Disturb" : discordStatus === "idle" ? "Idle" : "Offline";
            let activity = null;

            if (activities.length > 0) {
              const gameActivity = activities.find((act) => act.type === 0);
              if (gameActivity) {
                statusText = `Playing ${gameActivity.name}`;
                activity = gameActivity;
              }
            }

            setDiscordData({
              status: statusText,
              rawStatus: discordStatus,
              lastSeen: `${content[language].lastSeen} ${lastSeen}`,
              activity: activity,
            });
            break;
          } else {
            console.error(`Lanyard API failed (Attempt ${attempt}):`, data.error);
            if (attempt === retries) {
              setDiscordData({
                status: "Offline - API Error",
                rawStatus: "offline",
                lastSeen: "Unknown",
                activity: null,
              });
            }
          }
        } catch (error) {
          console.error(`Failed to fetch Discord data (Attempt ${attempt}):`, error);
          if (attempt === retries) {
            setDiscordData({
              status: "Offline - Network Error",
              rawStatus: "offline",
              lastSeen: "Unknown",
              activity: null,
            });
          }
        }
        if (attempt < retries) {
          await new Promise((resolve) => setTimeout(resolve, delay));
        }
      }
    };

    fetchDiscordData();
    const statusInterval = setInterval(fetchDiscordData, 30000);

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearInterval(typewriterInterval);
      clearInterval(statusInterval);
      document.removeEventListener("contextmenu", handleContextMenu);
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [videoFailed, videoKey, volume, language]);

  const handleClickToEnter = () => {
    setShowClickToEnter(false);
    if (audioRef.current) {
      audioRef.current.volume = volume;
      audioRef.current.play().then(() => {
        setIsPlaying(true);
        console.log("Audio played after user interaction");
      }).catch((error) => {
        console.error("Audio play failed after interaction:", error.message);
      });
    }
  };

  const handleLinkClick = (url, accountKey) => {
    if (videoRef.current) {
      videoRef.current.pause();
    }
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
    setTimeout(() => {
      window.location.href = url;
    }, 300);
  };

  const showUsernamePopup = (accountKey, url) => {
    if (accountKey && usernameMapping[accountKey]) {
      setPopupData({ username: usernameMapping[accountKey], url });
      setShowPopup(true);
      setCopyMessage("");
    }
  };

  const closePopup = () => {
    setShowPopup(false);
    setPopupData({ username: "", url: "" });
    setCopyMessage("");
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopyMessage(content[language].copiedMessage);
      setTimeout(() => setCopyMessage(""), 2000);
    }).catch((err) => {
      console.error("Failed to copy:", err);
      setCopyMessage(content[language].copyFailedMessage);
    });
  };

  const toggleAudio = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current.play().then(() => {
          setIsPlaying(true);
        }).catch((error) => {
          console.log("Audio play failed:", error);
        });
      }
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  };

  const handleMouseEnter = () => {
    setShowVolumeSlider(true);
  };

  const handleMouseLeave = () => {
    setShowVolumeSlider(false);
  };

  const toggleLanguageMenu = () => {
    setShowLanguageMenu((prev) => !prev);
  };

  const selectLanguage = (lang) => {
    setLanguage(lang);
    setShowLanguageMenu(false);
  };

  const loadIcon = (iconName, alt) => {
    const iconSrc = iconMapping[iconName];
    if (!iconSrc) {
      console.error(`Icon mapping not found for: ${iconName}`);
      return <img src={`https://via.placeholder.com/24?text=${alt}`} alt={alt} className="icon" />;
    }

    return (
      <img
        src={iconSrc}
        alt={alt}
        className="icon"
        onError={(e) => {
          console.error(`Failed to load icon: ${alt} at ${iconSrc}`);
          e.target.src = `https://via.placeholder.com/24?text=${alt}`;
        }}
        onLoad={() => console.log(`Successfully loaded icon: ${alt} at ${iconSrc}`)}
      />
    );
  };

  return (
    <div className="app-wrapper" style={{ direction: "rtl" }}>
      <style>
        {`
          html, body, * {
            cursor: url(${cursorImage}) 16 16, auto !important;
          }
          a, button, input[type="range"], .audio-player-wrapper, .play-pause-btn, .volume-slider, .click-to-enter-overlay, .language-switcher, .language-menu {
            cursor: url(${cursorImage}) 16 16, pointer !important;
          }
        `}
      </style>

      {videoFailed ? (
        <div className="background-image"></div>
      ) : (
        <video
          ref={videoRef}
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          className="background-video"
          onError={() => setVideoFailed(true)}
          key={videoKey}
        >
          <source src={backgroundVideo} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      )}

      <div className={`loading-screen ${isLoaded ? "fade-out" : ""}`}>
        <div className="astronaut-spinner">
          <img src={astronautSpinner} alt="Loading" className="spinner" />
        </div>
      </div>

      {showClickToEnter && (
        <div className="click-to-enter-overlay" onClick={handleClickToEnter}>
          <div className="click-to-enter-text">{content[language].clickToEnter}</div>
        </div>
      )}

      <div
        className="audio-player-wrapper"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className="audio-player">
          <audio ref={audioRef} loop>
            <source src={backgroundAudio} type="audio/mp3" />
            Your browser does not support the audio tag.
          </audio>
          <div className={`volume-slider-container ${showVolumeSlider ? "visible" : ""}`}>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={volume}
              onChange={handleVolumeChange}
              className="volume-slider form-range"
            />
          </div>
          <button onClick={toggleAudio} className="play-pause-btn btn">
            {isPlaying ? (
              <svg
                className="speaker-icon"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"
                  fill="rgba(255, 255, 255, 0.7)"
                />
              </svg>
            ) : (
              <svg
                className="muted-icon"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"
                  fill="rgba(255, 255, 255, 0.7)"
                />
                <path
                  d="M16 8L8 16M8 8l8 8"
                  stroke="rgba(255, 255, 255, 0.7)"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            )}
          </button>
        </div>
      </div>

      <div className="language-switcher">
        <button onClick={toggleLanguageMenu} className="language-btn">
          {content[language].languageLabel}
        </button>
        {showLanguageMenu && (
          <div className="language-menu">
            <div
              className="language-option"
              onClick={() => selectLanguage("ar")}
            >
              {content[language].languageArabic}
              {language === "ar" && (
                <svg
                  className="checkmark-icon"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M20 6L9 17L4 12"
                    stroke="#ffffff"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </div>
            <div
              className="language-option"
              onClick={() => selectLanguage("en")}
            >
              {content[language].languageEnglish}
              {language === "en" && (
                <svg
                  className="checkmark-icon"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M20 6L9 17L4 12"
                    stroke="#ffffff"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="container">
        {isLoaded && !showClickToEnter && (
          <>
            <div className={`profile slide-in ${language}`}>
              <div className="profile-pic-container">
                <img src={userProfile} alt="User Icon" className="profile-pic" />
                <div className={`status-indicator ${discordData.rawStatus}`}>
                  {discordData.rawStatus === "dnd" && (
                    <div className="status-icon dnd-icon"></div>
                  )}
                  {discordData.rawStatus === "idle" && (
                    <div className="status-icon idle-icon"></div>
                  )}
                  {discordData.rawStatus === "offline" && (
                    <div className="status-icon offline-icon"></div>
                  )}
                </div>
              </div>
              <div className="username-wrapper">
                {language === "ar" ? (
                  <>
                    <div className="discord-badges">
                      {badges.map((badge, index) => (
                        <img
                          key={index}
                          src={badge.url}
                          alt={badge.name}
                          className="badge-icon"
                          onError={(e) => {
                            console.error(`Failed to load badge: ${badge.name}`);
                            e.target.src = "https://via.placeholder.com/24?text=Badge"; // Fallback image
                          }}
                        />
                      ))}
                    </div>
                    <h1 className="username">Iburtum</h1>
                  </>
                ) : (
                  <>
                    <h1 className="username">Iburtum</h1>
                    <div className="discord-badges">
                      {badges.map((badge, index) => (
                        <img
                          key={index}
                          src={badge.url}
                          alt={badge.name}
                          className="badge-icon"
                          onError={(e) => {
                            console.error(`Failed to load badge: ${badge.name}`);
                            e.target.src = "https://via.placeholder.com/24?text=Badge"; // Fallback image
                          }}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
              <div className="bio white-text">
                <TypewriterText text={content[language].welcomeMessage} delay={100} />
              </div>
              <div className="discord-info">
                <div className="discord-status">{discordData.status}</div>
                {discordData.rawStatus === "offline" && (
                  <div className="discord-last-seen">{discordData.lastSeen}</div>
                )}
                {discordData.activity && (
                  <div className="discord-activity">
                    {discordData.activity.name} - {discordData.activity.details || "Playing"}
                  </div>
                )}
              </div>
            </div>

            <div className="links fade-in d-flex flex-wrap justify-content-center">
              <a
                href="https://discord.iburtum.xyz"
                className="m-2"
                onClick={(e) => {
                  e.preventDefault();
                  handleLinkClick("https://discord.iburtum.xyz", "discord");
                }}
              >
                {loadIcon("discord", content[language].discord)}
              </a>
              <a
                href="https://steamcommunity.com/id/iburtum"
                target="_blank"
                rel="noopener noreferrer"
                className="m-2"
                onClick={(e) => {
                  e.preventDefault();
                  handleLinkClick("https://steamcommunity.com/id/iburtum", "steam");
                }}
              >
                {loadIcon("steam", content[language].steam)}
              </a>
              <a
                href="https://bit.ly/3PBDjkU"
                target="_blank"
                rel="noopener noreferrer"
                className="m-2"
                onClick={(e) => {
                  e.preventDefault();
                  handleLinkClick("https://bit.ly/3PBDjkU");
                }}
              >
                {loadIcon("gamepad", content[language].gameList)}
              </a>
              <a
                href="https://iburtum.xyz"
                target="_blank"
                rel="noopener noreferrer"
                className="m-2"
                onClick={(e) => {
                  e.preventDefault();
                  handleLinkClick("https://iburtum.xyz");
                }}
              >
                {loadIcon("globe", content[language].website)}
              </a>
              <a
                href="https://blog.iburtum.xyz/blog/topwaitedgamesfor2025-ls2"
                target="_blank"
                rel="noopener noreferrer"
                className="m-2"
                onClick={(e) => {
                  e.preventDefault();
                  handleLinkClick("https://blog.iburtum.xyz/blog/topwaitedgamesfor2025-ls2");
                }}
              >
                {loadIcon("blog", content[language].blog)}
              </a>
              <a
                href="https://x.com/iburtum"
                target="_blank"
                rel="noopener noreferrer"
                className="m-2"
                onClick={(e) => {
                  e.preventDefault();
                  handleLinkClick("https://x.com/iburtum");
                }}
              >
                {loadIcon("twitter", content[language].xAccount)}
              </a>
              <a
                href="#"
                className="m-2"
                onClick={(e) => {
                  e.preventDefault();
                  showUsernamePopup("riot", "https://www.riotgames.com/");
                }}
              >
                {loadIcon("riot", content[language].riotAccount)}
              </a>
              <a
                href="#"
                className="m-2"
                onClick={(e) => {
                  e.preventDefault();
                  showUsernamePopup("blizzard", "https://www.blizzard.com/");
                }}
              >
                {loadIcon("blizzard", content[language].blizzardAccount)}
              </a>
              <a
                href="#"
                className="m-2"
                onClick={(e) => {
                  e.preventDefault();
                  showUsernamePopup("xbox", "https://www.xbox.com/");
                }}
              >
                {loadIcon("xbox", content[language].xboxAccount)}
              </a>
              <a
                href="#"
                className="m-2"
                onClick={(e) => {
                  e.preventDefault();
                  showUsernamePopup("playstation", "https://www.playstation.com/");
                }}
              >
                {loadIcon("playstation", content[language].playstationAccount)}
              </a>
            </div>

            {showPopup && (
              <div className="username-popup">
                <div className="username-popup-content">
                  <span className="close-popup" onClick={closePopup}>
                    ×
                  </span>
                  <h3>{content[language].usernameLabel}</h3>
                  <p>{popupData.username}</p>
                  <button
                    className="copy-btn"
                    onClick={() => copyToClipboard(popupData.username)}
                  >
                    {content[language].copyButton}
                  </button>
                  {copyMessage && <p className="copy-message">{copyMessage}</p>}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const TypewriterText = ({ text, delay = 100 }) => {
  const [displayText, setDisplayText] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTypingForward, setIsTypingForward] = useState(true);
  const [showCursor, setShowCursor] = useState(true);

  useEffect(() => {
    const cursorInterval = setInterval(() => {
      setShowCursor((prev) => !prev);
    }, 500);

    let timeout;

    if (isTypingForward) {
      if (currentIndex < text.length) {
        timeout = setTimeout(() => {
          setDisplayText((prev) => prev + text[currentIndex]);
          setCurrentIndex((prev) => prev + 1);
        }, delay);
      } else {
        timeout = setTimeout(() => {
          setIsTypingForward(false);
        }, 2000);
      }
    } else {
      if (currentIndex > 0) {
        timeout = setTimeout(() => {
          setDisplayText((prev) => prev.slice(0, -1));
          setCurrentIndex((prev) => prev - 1);
        }, delay / 2);
      } else {
        timeout = setTimeout(() => {
          setIsTypingForward(true);
        }, 1000);
      }
    }

    return () => {
      clearTimeout(timeout);
      clearInterval(cursorInterval);
    };
  }, [currentIndex, text, delay, isTypingForward]);

  return (
    <span>
      {displayText}
      <span className="typewriter-cursor">{showCursor ? "|" : " "}</span>
    </span>
  );
};

export default App;